import React, { useMemo } from 'react';
import { PublicSetCardAll } from 'store/services/sets/publicSetsApi';
import './RarityBreakdown.css';

export interface RarityCounts {
  mythic: number;
  rare: number;
  uncommon: number;
  common: number;
  special: number;
}

export function computeRarityCounts(cards: PublicSetCardAll[]): RarityCounts {
  const acc: RarityCounts = { mythic: 0, rare: 0, uncommon: 0, common: 0, special: 0 };
  for (const c of cards) {
    const r = (c.rarity || '').toLowerCase();
    if (r === 'mythic' || r === 'rare' || r === 'uncommon' || r === 'common') {
      acc[r] += 1;
    } else if (r) {
      acc.special += 1;
    }
  }
  return acc;
}

interface RarityBreakdownProps {
  cards: PublicSetCardAll[];
  totalCards: number;
}

interface CellConfig {
  key: keyof RarityCounts;
  label: string;
  className: string;
}

const CELLS: CellConfig[] = [
  { key: 'mythic', label: 'Mythic', className: 'rarity-cell-mythic' },
  { key: 'rare', label: 'Rare', className: 'rarity-cell-rare' },
  { key: 'uncommon', label: 'Uncommon', className: 'rarity-cell-uncommon' },
  { key: 'common', label: 'Common', className: 'rarity-cell-common' },
  { key: 'special', label: 'Special', className: 'rarity-cell-special' },
];

const formatPct = (count: number, total: number): string => {
  if (!total) return '0%';
  const pct = (count / total) * 100;
  return pct >= 10 ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`;
};

export const RarityBreakdown: React.FC<RarityBreakdownProps> = ({ cards, totalCards }) => {
  const counts = useMemo(() => computeRarityCounts(cards), [cards]);

  const visibleCells = CELLS.filter((c) => c.key !== 'special' || counts.special > 0);

  return (
    <section className="rarity-breakdown" aria-label="Rarity breakdown">
      <h2 className="rarity-breakdown-title">Rarity Breakdown</h2>
      <div className="rarity-breakdown-strip">
        {visibleCells.map((cell) => (
          <div key={cell.key} className={`rarity-cell ${cell.className}`}>
            <span className="rarity-cell-label">{cell.label}</span>
            <span className="rarity-cell-count">{counts[cell.key].toLocaleString()}</span>
            <span className="rarity-cell-pct">{formatPct(counts[cell.key], totalCards)}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
